import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Loader2, Mail, Lock, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // States for forgot password form
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // States for reset password form
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState('');

  // If no token, show "forgot password" form
  // If token present, show "new password" form

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setSendingEmail(true);
    setError('');
    
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Email inviata! Controlla la tua casella di posta.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore durante l\'invio');
      toast.error('Errore durante l\'invio');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Le password non coincidono');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('La password deve essere di almeno 8 caratteri');
      return;
    }
    
    setResetting(true);
    
    try {
      await axios.post(`${API}/api/auth/reset-password`, { 
        token, 
        password: newPassword 
      });
      setResetSuccess(true);
      toast.success('Password reimpostata con successo!');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Errore durante il reset';
      setError(detail);
      toast.error(detail);
    } finally {
      setResetting(false);
    }
  };

  // Success state after password reset
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Password Reimpostata!</CardTitle>
            <CardDescription className="text-base mt-2">
              La tua password è stata modificata con successo.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full bg-[#0055BB] hover:bg-[#004499]"
              onClick={() => navigate('/login')}
            >
              Vai al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email sent confirmation
  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Controlla la tua email</CardTitle>
            <CardDescription className="text-base mt-2">
              Abbiamo inviato un link per reimpostare la password a:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-100 rounded-lg p-3 text-center">
              <span className="font-medium text-slate-700">{email}</span>
            </div>
            <p className="text-sm text-slate-600 text-center">
              Il link è valido per 1 ora. Se non trovi l'email, controlla la cartella spam.
            </p>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => setEmailSent(false)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Indietro
              </Button>
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => navigate('/login')}
              >
                Vai al Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset password form (when token is present)
  if (token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-[#0055BB]/10 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-[#0055BB]" />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Nuova Password
            </CardTitle>
            <CardDescription>
              Inserisci la tua nuova password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="password">Nuova Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimo 8 caratteri"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pr-10"
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirm">Conferma Password</Label>
                <Input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Ripeti la password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-[#0055BB] hover:bg-[#004499]"
                disabled={resetting}
              >
                {resetting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reimpostazione...</>
                ) : (
                  'Reimposta Password'
                )}
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-[#0055BB] hover:underline">
                  Torna al Login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
        
        <div className="fixed bottom-4 text-center w-full">
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-[#0055BB]">Assietica Gest</span> – by Assietica
          </p>
        </div>
      </div>
    );
  }

  // Forgot password form (no token)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#0055BB]/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-[#0055BB]" />
          </div>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Password Dimenticata?
          </CardTitle>
          <CardDescription>
            Inserisci la tua email per ricevere un link di reset
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleForgotPassword} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="La tua email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#0055BB] hover:bg-[#004499]"
              disabled={sendingEmail}
            >
              {sendingEmail ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Invio in corso...</>
              ) : (
                'Invia Link di Reset'
              )}
            </Button>
            
            <div className="text-center">
              <Link to="/login" className="text-sm text-[#0055BB] hover:underline flex items-center justify-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Torna al Login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
      
      <div className="fixed bottom-4 text-center w-full">
        <p className="text-sm text-slate-500">
          <span className="font-semibold text-[#0055BB]">Assietica Gest</span> – by Assietica
        </p>
      </div>
    </div>
  );
}
